// src/ryan/admin/components/tabs/RyanAdminLeads.tsx
//
// "Leads Pendentes" — ferramenta de contato direto (WhatsApp) com quem se
// identificou no checkout (nome/e-mail/WhatsApp) mas nunca completou o
// pagamento (status pending/awaiting_payment/payment_failed/rejected em
// RyanAccessRequest). Deliberadamente SEPARADA da aba Analytics: aquela usa
// RyanAnalyticsEvent, 100% anônimo por decisão de LGPD da Fase 0 — esta usa
// dado pessoal fornecido de propósito pra completar uma compra, com retenção
// de 90 dias via job diário auditável (ver server/jobs/ryanLeadsCleanupJobs.js).
// Nenhum código/rota compartilhado com o namespace /admin/analytics/*.
//
// Montado como um MODO dentro do select de RyanAdminAnalytics.tsx (não é
// mais uma aba própria do hub) — por isso sem o wrapper .ras-pane próprio
// (o pai já fornece) e sem select de produto/período próprios: productId
// (opcional — string vazia = todos os produtos) e period agora são props
// controladas pelo pai, que os renderiza na MESMA linha do select de modo
// (antes eram uma segunda linha de filtros só deste componente, quebrando o
// layout em duas linhas). Paginação, exclusão e o resto continuam 100%
// internos — só o produto/período saiu daqui.

import React, { useCallback, useEffect, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import './RyanAdminLeads.css';

const tok = () => localStorage.getItem('accessToken') || '';
const api = (path: string, opts?: RequestInit) =>
    fetch(`/api/ryan${path}`, { ...opts, headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json', ...(opts?.headers || {}) } });

interface Lead {
    _id: string;
    name: string;
    email: string;
    whatsapp: string;
    productTitle: string;
    status: 'pending' | 'awaiting_payment' | 'payment_failed' | 'rejected';
    createdAt: string;
}

interface LeadsResponse {
    leads: Lead[];
    total: number;
    page: number;
    pageSize: number;
    period: string;
}

const STATUS_LABELS: Record<Lead['status'], string> = {
    pending: 'Pendente',
    awaiting_payment: 'Aguardando pagamento',
    payment_failed: 'Pagamento falhou',
    rejected: 'Rejeitado',
};

function formatDateTime(iso: string): string {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function buildWhatsappUrl(number: string, name: string, productTitle: string): string {
    const cleaned = (number || '').replace(/\D/g, '');
    const full = cleaned.startsWith('55') ? cleaned : `55${cleaned}`;
    const msg = encodeURIComponent(
        `Oi, ${name}! Vi que você começou a se inscrever em "${productTitle}" mas o pagamento não foi concluído. Posso te ajudar a finalizar?`
    );
    return `https://wa.me/${full}?text=${msg}`;
}

interface RyanAdminLeadsProps {
    productId: string;
    period: string;
}

const RyanAdminLeads: React.FC<RyanAdminLeadsProps> = ({ productId, period }) => {
    const [page, setPage] = useState(1);
    const [data, setData] = useState<LeadsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    // Exclusão pontual — sempre um lead não-convertido por definição (a
    // listagem já é restrita aos 4 status não-aprovados), então um clique +
    // confirmação inline básica basta (sem a complexidade de 3 níveis usada
    // na exclusão de sessão de analytics).
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    useEffect(() => { setPage(1); }, [productId, period]);

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({ period, page: String(page), pageSize: '25' });
        if (productId) params.set('productId', productId);
        return api(`/admin/leads?${params.toString()}`)
            .then(r => r.ok ? r.json() : null)
            .then(setData)
            .finally(() => setLoading(false));
    }, [productId, period, page]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async (id: string) => {
        setDeletingId(id);
        try {
            await api(`/admin/leads/${id}`, { method: 'DELETE' });
            setDeleteConfirmId(null);
            // Se essa era a última linha da página atual (e não a primeira
            // página), volta uma página — senão a lista ficaria vazia com
            // resultados ainda existindo numa página anterior.
            if (data && data.leads.length === 1 && page > 1) {
                setPage(p => p - 1);
            } else {
                await load();
            }
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="ras-section rana-card">
                {loading || !data ? (
                    <div className="ras-spinner-wrap"><div className="ras-spinner" /></div>
                ) : data.leads.length === 0 ? (
                    <div className="rana-empty">Nenhum lead pendente neste período/filtro.</div>
                ) : (
                    <>
                        <div className="rana-sessions-table-wrap">
                            <table className="rana-sessions-table">
                                <thead>
                                    <tr>
                                        <th>Nome</th><th>E-mail</th><th>WhatsApp</th>
                                        <th>Produto</th><th>Status</th><th>Criado em</th><th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.leads.map(lead => (
                                        <tr key={lead._id}>
                                            <td>{lead.name}</td>
                                            <td>{lead.email}</td>
                                            <td>{lead.whatsapp || '—'}</td>
                                            <td>{lead.productTitle}</td>
                                            <td>
                                                <span className={`rana-outcome-badge ral-status--${lead.status}`}>
                                                    {STATUS_LABELS[lead.status]}
                                                </span>
                                            </td>
                                            <td>{formatDateTime(lead.createdAt)}</td>
                                            <td>
                                                <div className="ral-actions">
                                                    {lead.whatsapp ? (
                                                        <a
                                                            className="ras-grant-btn ral-whatsapp-btn"
                                                            href={buildWhatsappUrl(lead.whatsapp, lead.name, lead.productTitle)}
                                                            target="_blank" rel="noopener noreferrer"
                                                        >
                                                            WhatsApp
                                                        </a>
                                                    ) : '—'}
                                                    {deleteConfirmId === lead._id ? (
                                                        <span className="ral-delete-confirm">
                                                            <button
                                                                type="button" className="ral-delete-confirm-yes"
                                                                onClick={() => handleDelete(lead._id)}
                                                                disabled={deletingId === lead._id}
                                                            >
                                                                {deletingId === lead._id ? '...' : 'Sim'}
                                                            </button>
                                                            <button
                                                                type="button" className="ral-icon-btn"
                                                                onClick={() => setDeleteConfirmId(null)}
                                                                disabled={deletingId === lead._id}
                                                            >
                                                                <X size={13} />
                                                            </button>
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button" className="ral-icon-btn ral-icon-btn--danger"
                                                            onClick={() => setDeleteConfirmId(lead._id)}
                                                            title="Excluir lead"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="rana-pagination">
                            <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Anterior</button>
                            <span>{page} de {Math.max(1, Math.ceil(data.total / data.pageSize))} ({data.total.toLocaleString('pt-BR')} leads)</span>
                            <button type="button" disabled={page * data.pageSize >= data.total} onClick={() => setPage(p => p + 1)}>Próxima →</button>
                        </div>
                    </>
                )}
        </div>
    );
};

export default RyanAdminLeads;
